import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Router } from '@angular/router';
import { BaseFormComponent } from '@core/components/base-form.component';
import { MarcatoreCorpoAbilitatoDto, MarcatoreDto } from '@core/http-gen/models';
import { MarcatoreService } from '@core/http-gen/services';
import { ToastService } from '@core/services/toast.service';
import { tipoMarcatoreList, statoMarcatoreList } from '@core/utils/marcatore.utils';
import { ConfirmationService } from 'primeng/api';
import { confirmDialogConfig } from '@core/utils/dialog-config';
import { disable } from 'ol/rotationconstraint';
@Component({
    selector: 'app-edit-marcatore',
    templateUrl: './edit-marcatore.component.html',
    styleUrl: './edit-marcatore.component.scss',
})
export class EditMarcatoreComponent extends BaseFormComponent implements OnInit {
    public formGroup: FormGroup;
    public corpiAbilitati: { value: number; label: string }[] = [];
    public marcatoreId: null | number = null;

    public tipoMarcatoreList = tipoMarcatoreList;
    public statoMarcatoreList = statoMarcatoreList;

    constructor(
        private formBuilder: FormBuilder,
        private activatedRoute: ActivatedRoute,
        private route: Router,
        private marcatoreService: MarcatoreService,
        private confirmationService: ConfirmationService,
        private toastService: ToastService,
    ) {
        super();

        this.formGroup = this.formBuilder.group({
            nome: [null, [Validators.required, Validators.minLength(3)]],
            corpoId: [null, [Validators.required]],
            tipoMarcatore: [null, [Validators.required, Validators.pattern(/^\d+$/)]],
            descrizione: [null, Validators.minLength(3)],
            ultimoControllo: [null],
            statoMarcatore: [null],
            posizione: this.formBuilder.group({
                x: [null, [Validators.required]],
                y: [null, [Validators.required]],
            }),
        });

        this.formGroup.get('statoMarcatore')?.disable();
    }

    ngOnInit(): void {
        this.marcatoreId = this.activatedRoute.snapshot.params['id'];
        this.setCorpiAbilitati();
        this.getMarcatore();
    }

    private redirectToMap(): void {
        const mapZoom = this.activatedRoute.snapshot.queryParams['zoom'] || null;
        const mapCenterLon = this.activatedRoute.snapshot.queryParams['centerLon'] || null;
        const mapCenterLat = this.activatedRoute.snapshot.queryParams['centerLat'] || null;

        this.route.navigate(['/mappa'], {
            queryParams: {
                zoom: mapZoom,
                centerLon: mapCenterLon,
                centerLat: mapCenterLat,
            },
        });
    }

    public getMarcatore(): void {
        if (!this.marcatoreId) {
            this.toastService.error('Marcatore non trovato');
            this.route.navigate(['/mappa']);
            return;
        }

        this.marcatoreService.apiMarcatoreMarcatoreByIdGet({ marcatoreId: this.marcatoreId }).subscribe((marcatore: MarcatoreDto) => {
            this.formGroup.patchValue({
                nome: marcatore.nome,
                corpoId: marcatore.corpoId,
                tipoMarcatore: marcatore.tipoMarcatore,
                descrizione: marcatore.descrizione,
                ultimoControllo: marcatore.ultimoControllo ? new Date(marcatore.ultimoControllo).toISOString().split('T')[0] : null,
                statoMarcatore: marcatore.statoMarcatore,
                posizione: {
                    x: Math.round(marcatore.posizione.x * 100000) / 100000,
                    y: Math.round(marcatore.posizione.y * 100000) / 100000,
                },
            });
        });
    }

    /** Carica le coordinate dall'url, se disponibili altrimenti non fa nulla */
    public generateNewName(): void {
        const tipoMarcatore = this.formGroup.get('tipoMarcatore')?.value;
        const tipoMarcatoreLabel = this.tipoMarcatoreList.find((item) => item.value === tipoMarcatore)?.label;

        if (tipoMarcatore) {
            this.formGroup.get('nome')?.setValue(tipoMarcatoreLabel);
        } else {
            console.error('tipo marcatore non disponibile per generare il nome');
        }
    }

    public onSubmit(): void {
        if (this.formGroup.invalid) {
            this.formGroup.markAllAsTouched();
            console.error('Form non valido:', this.formGroup.hasError);
            return;
        }

        if (!this.marcatoreId) {
            return;
        }

        this.marcatoreService
            .apiMarcatoreMarcatoreEditPost({
                body: {
                    id: this.marcatoreId,
                    corpoId: this.formGroup.get('corpoId')?.value,
                    nome: this.formGroup.get('nome')?.value,
                    tipoMarcatore: this.formGroup.get('tipoMarcatore')?.value,
                    descrizione: this.formGroup.get('descrizione')?.value,
                    ultimoControllo: this.formGroup.get('ultimoControllo')?.value,
                    statoMarcatore: this.formGroup.get('statoMarcatore')?.value,
                    posizione: {
                        x: this.formGroup.get('posizione.x')?.value,
                        y: this.formGroup.get('posizione.y')?.value,
                    },
                },
            })
            .subscribe(() => {
                this.toastService.success('Marcatore aggiornato con successo');
                this.redirectToMap();
            });
    }

    public showConfirmDelete(): void {
        if (!this.marcatoreId) {
            return;
        }

        this.confirmationService.confirm({
            message: 'Voi eliminare il marcatore? Questa operazione non è reversibile.',
            header: 'Eliminazione marcatore',
            acceptLabel: 'Elimina',
            rejectLabel: 'Annulla',
            icon: 'pi pi-info-circle',
            ...confirmDialogConfig,
            accept: () => {
                this.delete();
            },
        });
    }

    public delete(): void {
        if (!this.marcatoreId) {
            return;
        }

        this.marcatoreService
            .apiMarcatoreMarcatoreDeleteDelete({
                marcatoreId: this.marcatoreId,
            })
            .subscribe(() => {
                this.toastService.success('Marcatore eliminato con successo');
                this.redirectToMap();
            });
    }

    private setCorpiAbilitati(): void {
        this.marcatoreService.apiMarcatoreMarcatoreCorpiAbilitatiGet({}).subscribe((corpiAbilitati) => {
            if (!corpiAbilitati) {
                this.toastService.error('Non puoi modificare il marcatore');
                this.route.navigate(['/mappa']);
                return;
            }

            this.corpiAbilitati = corpiAbilitati.map((corpo) => ({
                value: corpo.id,
                label: corpo.name,
            }));
        });
    }

    public onCancel(): void {
        this.redirectToMap();
    }
}
